// Date utilities. We work with local Date objects and YYYY-MM-DD strings.

export type CalView = 'day' | 'week' | 'month' | 'quarter' | 'year'

export function pad(n: number): string {
  return String(n).padStart(2, '0')
}

export function ymd(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function parseYmd(s: string): Date {
  const [y, m, d] = s.split('-').map(Number)
  return new Date(y, m - 1, d)
}

export function todayYmd(): string {
  return ymd(new Date())
}

export function addDays(d: Date, n: number): Date {
  const x = new Date(d)
  x.setDate(x.getDate() + n)
  return x
}

export function addMonths(d: Date, n: number): Date {
  const x = new Date(d.getFullYear(), d.getMonth() + n, 1)
  return x
}

// Monday-based week start.
export function startOfWeek(d: Date): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const day = (x.getDay() + 6) % 7
  x.setDate(x.getDate() - day)
  return x
}

export function endOfWeek(d: Date): Date {
  return addDays(startOfWeek(d), 6)
}

export function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1)
}

export function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0)
}

export function startOfQuarter(d: Date): Date {
  return new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1)
}

export function quarterOf(d: Date): number {
  return Math.floor(d.getMonth() / 3) + 1
}

// Active locale for all date formatting (set by the i18n provider).
let dateLocale = 'fi-FI'
export function setDateLocale(locale: string): void {
  dateLocale = locale
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

// 2024-01-01 is a Monday — used to build Monday-first weekday labels.
const MONDAY = new Date(2024, 0, 1)

export function weekdayLabels(): string[] {
  const fmt = new Intl.DateTimeFormat(dateLocale, { weekday: 'short' })
  return Array.from({ length: 7 }, (_, i) => cap(fmt.format(addDays(MONDAY, i))))
}

export function weekdayNarrow(): string[] {
  const fmt = new Intl.DateTimeFormat(dateLocale, { weekday: 'narrow' })
  return Array.from({ length: 7 }, (_, i) => cap(fmt.format(addDays(MONDAY, i))))
}

export function monthLong(m: number): string {
  return cap(new Intl.DateTimeFormat(dateLocale, { month: 'long' }).format(new Date(2021, m, 1)))
}

export function formatLong(s: string): string {
  const d = parseYmd(s)
  return cap(
    new Intl.DateTimeFormat(dateLocale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(d),
  )
}

export function formatShort(d: Date): string {
  return new Intl.DateTimeFormat(dateLocale, { day: 'numeric', month: 'short' }).format(d)
}

export function formatHour(h: number): string {
  return `${pad(h)}:00`
}

export function formatHourRange(start: number, end: number): string {
  return `${pad(start)}:00–${pad(end)}:00`
}

// A 6×7 matrix of dates for a month grid (Mon–Sun columns).
export function monthMatrix(year: number, month: number): Date[][] {
  let cur = startOfWeek(new Date(year, month, 1))
  const weeks: Date[][] = []
  for (let w = 0; w < 6; w++) {
    const row: Date[] = []
    for (let d = 0; d < 7; d++) {
      row.push(cur)
      cur = addDays(cur, 1)
    }
    weeks.push(row)
  }
  return weeks
}

// Date range (week-padded) to fetch for a given view + anchor.
export function rangeFor(view: CalView, anchor: Date): { from: string; to: string } {
  switch (view) {
    case 'day':
      return { from: ymd(anchor), to: ymd(anchor) }
    case 'week':
      return { from: ymd(startOfWeek(anchor)), to: ymd(endOfWeek(anchor)) }
    case 'month':
      return { from: ymd(startOfWeek(startOfMonth(anchor))), to: ymd(endOfWeek(endOfMonth(anchor))) }
    case 'quarter': {
      const qs = startOfQuarter(anchor)
      const qe = endOfMonth(addMonths(qs, 2))
      return { from: ymd(startOfWeek(qs)), to: ymd(endOfWeek(qe)) }
    }
    case 'year': {
      const ys = new Date(anchor.getFullYear(), 0, 1)
      const ye = new Date(anchor.getFullYear(), 11, 31)
      return { from: ymd(startOfWeek(ys)), to: ymd(endOfWeek(ye)) }
    }
  }
}

export function navAnchor(view: CalView, anchor: Date, dir: number): Date {
  switch (view) {
    case 'day':
      return addDays(anchor, dir)
    case 'week':
      return addDays(anchor, dir * 7)
    case 'month':
      return addMonths(anchor, dir)
    case 'quarter':
      return addMonths(anchor, dir * 3)
    case 'year':
      return addMonths(anchor, dir * 12)
  }
}

export function viewTitle(view: CalView, anchor: Date): string {
  switch (view) {
    case 'day':
      return formatLong(ymd(anchor))
    case 'week': {
      const s = startOfWeek(anchor)
      return `${formatShort(s)} – ${formatShort(addDays(s, 6))}, ${s.getFullYear()}`
    }
    case 'month':
      return `${monthLong(anchor.getMonth())} ${anchor.getFullYear()}`
    case 'quarter':
      return `Q${quarterOf(anchor)} ${anchor.getFullYear()}`
    case 'year':
      return String(anchor.getFullYear())
  }
}
